<template>
  <div class="app-input" :class="{ 'has-error': error && error.length }">
    <label v-if="label" :id="label">{{ label }}</label>

    <div class="app-input-wrap">
      <div
        class="start-icon"
        @click.stop="emitIfClickableStart"
        v-if="startIcon"
        v-html="$icon(startIcon)"
      ></div>

      <input
        :type="type"
        v-model="newValue"
        :id="label || id"
        :autofocus="autofocus"
        :value="isDatePicker ? getDateFormat : newValue"
        :disabled="disabled || loading"
        :placeholder="placeholder"
        ref="input"
        :class="{ 'has-start-icon': startIcon }"
        @focus="$emit('focus', $event)"
        @blur="$emit('blur', $event)"
        @input="onInput"
      />

      <div
        class="icon"
        @click.stop="emitIfClickable"
        :class="{ clickable: iconClickable }"
        v-if="icon"
        v-html="$icon(icon)"
      ></div>
      <div class="loader" v-if="loading" v-html="$icon('loader')"></div>
    </div>

    <p class="message" v-if="message">{{ message }}</p>
    <p class="error" v-if="error">{{ error }}</p>
  </div>
</template>

<script>
import { nextTick } from "vue";

export default {
  name: "app-input",

  emits: [
    "update:modelValue",
    "click",
    "start-click",
    "focus",
    "blur",
    "change",
  ],

  props: {
    type: {
      type: String,
      default: "text",
    },
    address: {
      type: Boolean,
      default: false,
    },
    shortAddress: {
      type: Boolean,
      default: false,
    },
    iconClickable: {
      type: Boolean,
      default: false,
    },
    modelValue: [String, Number],
    icon: [String, Boolean],
    startIcon: [String, Boolean],
    dateFormat: {
      type: [String, Boolean],
      default: false,
    },
    label: String,
    id: String,
    value: [String],
    placeholder: String,
    message: String,
    error: String,
    autofocus: Boolean,
    isDatePicker: Boolean,
    isTimePicker: Boolean,
    loading: false,
    disabled: false,
  },

  data() {
    return {
      newValue: this.modelValue,
      focused: false,
      autoComplete: null,
    };
  },

  mounted() {
    nextTick(() => {
      if (this.address) {
        this.autocomplete = new google.maps.places.Autocomplete(
          this.$refs.input
        );
        // this.autocomplete.setComponentRestrictions({
        //     country: ["IL"],
        // });

        this.autocomplete.addListener("place_changed", () => {
          const place = this.autocomplete.getPlace();

          // console.log(place);

          let extra = {
            city: "",
            state: "",
            country: "",
            country_long: "",
            street_number: "",
            route: "",
            zip: null,
            lat: null,
            lng: null,
          };

          place.address_components.forEach(function (comp) {
            if (
              comp.types.includes("locality") ||
              comp.types.includes("sublocality")
            ) {
              extra.city = comp.long_name;
            }
            if (comp.types.includes("country")) {
              extra.country = comp.short_name;
              extra.country_long = comp.long_name;
            }
            if (comp.types.includes("administrative_area_level_1")) {
              extra.state = comp.short_name;
            }
            if (comp.types.includes("postal_code")) {
              extra.zip = comp.long_name;
            }
            if (comp.types.includes("street_number")) {
              extra.street_number = comp.long_name;
            }
            if (comp.types.includes("route")) {
              extra.route = comp.long_name;
            }
          });

          extra.lat = place.geometry.location.lat();
          extra.lng = place.geometry.location.lng();

          let parts = [];

          if (place.business_status) {
            let address = [`${place.name}`];

            if (extra.city && !place.name.includes(extra.city)) {
              address.push(extra.city);
            }

            this.newValue = address.join(", ");
            this.onInput();

            return;
          }

          if (this.shortAddress) {
            let address = "";
            let street = "";

            if (extra.route) {
              street = `${extra.route}`;
              address += `${extra.route}`;
            }

            if (extra.street_number) {
              address += ` ${extra.street_number}`;
              street += ` ${extra.street_number}`;
              parts.push(street);

              if (extra.city) {
                address += ` ${extra.city}`;
                parts.push(extra.city);
              }
            } else {
              parts.push(street);
            }

            if (extra.city && !extra.street_number) {
              address += ` ${extra.city}`;
              parts.push(extra.city);

              if (extra.country && extra.country !== "IL") {
                address += ` `;
              }
            }

            console.log(parts);
            if (extra.country && extra.country !== "IL") {
              address += `${extra.country_long}`;
              parts.push(extra.country_long);
            }

            this.newValue = parts.join(" ");
          } else if (place.formatted_address) {
            this.newValue = place.formatted_address;
          }

          this.onInput();
        });
      }
    });
  },

  watch: {
    modelValue() {
      this.newValue = this.modelValue;
    },
  },

  methods: {
    emitIfClickable() {
      if (!this.iconClickable) {
        return;
      }

      this.$emit("click");
    },

    emitIfClickableStart() {
      if (!this.iconClickable) {
        return;
      }

      if (this.isDatePicker || this.isTimePicker) {
        this.$refs.input.click();
      }

      this.$emit("start-click");
    },

    onInput() {
      if (this.isDatePicker && this.moment(this.newValue).isValid()) {
        this.$emit(
          "update:modelValue",
          this.moment(this.newValue).toISOString(true)
        );
        this.$emit("change", this.moment(this.newValue).toISOString(true));
        return;
      }

      this.$emit("update:modelValue", this.newValue);
      this.$emit("change", this.newValue);
    },
  },

  computed: {
    filled() {
      return this.newValue || this.focused;
    },

    getDateFormat() {
      return this.moment(this.newValue).isValid()
        ? this.moment(this.newValue).format(this.dateFormat)
        : null;
    },
  },
};
</script>

<style lang="scss" scoped>
.app-input {
  .loader {
    ::v-deep svg {
      stroke: black;
    }
  }
}
.en-class .app-input input.has-start-icon,
.app-input textarea.has-start-icon {
  padding-left: 40px;
}

.en-class .app-input .start-icon {
  // right: 10px;
  left: 10px;
}

.en-class .app-input .icon {
  right: 11px !important;
}
.he-class .app-input .icon {
  left: 10px;
}
</style>
